import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import AnimatedNumber from "@/components/ui-kit/AnimatedNumber";
import Cta from "@/components/ui-kit/Cta";
import { trackCalculator } from "@/lib/analytics";

const FEE_RATE = 0.005; // 0.5 percent hard ceiling

const usd0 = (n) => `$${Math.max(0, Math.round(n)).toLocaleString("en-US")}`;
const usd2 = (n) =>
  `$${Math.max(0, n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Live income calculator. `variant` controls how much is shown.
// mini: home teaser. full: pricing page.
export default function IncomeCalculator({ variant = "full" }) {
  const [income, setIncome] = useState(6000);
  const [pct, setPct] = useState(25);
  const [touched, setTouched] = useState(false);

  const { routed, fee, stays } = useMemo(() => {
    const safeIncome = Math.max(0, income);
    const safePct = Math.min(100, Math.max(0, pct));
    const routedAmt = (safeIncome * safePct) / 100;
    const feeAmt = routedAmt * FEE_RATE;
    const staysAmt = Math.max(0, safeIncome - routedAmt - feeAmt);
    return { routed: routedAmt, fee: feeAmt, stays: staysAmt };
  }, [income, pct]);

  const onUse = () => {
    if (!touched) {
      setTouched(true);
      trackCalculator(variant === "mini" ? "home-income" : "pricing-income");
    }
  };

  return (
    <div
      className="rounded-3xl border border-etap-deep-100 bg-white p-6 shadow-etap-lg sm:p-8"
      data-testid={variant === "mini" ? "home-calculator" : "pricing-calculator"}
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="calc-income" className="flex items-center justify-between text-sm font-semibold text-etap-deep-700">
            Monthly income
            <span className="font-display text-base text-etap-deep-800">{usd0(income)}</span>
          </label>
          <Slider
            id="calc-income"
            data-testid="calc-income-slider"
            aria-label="Monthly income"
            className="mt-4 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-etap-primary [&_[role=slider]]:bg-white [&_.bg-primary]:bg-etap-primary [&_.bg-primary\\/20]:bg-etap-primary-100"
            min={0}
            max={30000}
            step={100}
            value={[income]}
            onValueChange={(v) => {
              setIncome(v[0]);
              onUse();
            }}
          />
          <p className="mt-2 text-xs text-etap-deep-400">$0 to $30,000</p>
        </div>

        <div>
          <label htmlFor="calc-pct" className="flex items-center justify-between text-sm font-semibold text-etap-deep-700">
            Set aside percentage
            <span className="font-display text-base text-etap-deep-800">{pct}%</span>
          </label>
          <Slider
            id="calc-pct"
            data-testid="calc-pct-slider"
            aria-label="Set aside percentage"
            className="mt-4 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-etap-primary [&_[role=slider]]:bg-white [&_.bg-primary]:bg-etap-primary [&_.bg-primary\\/20]:bg-etap-primary-100"
            min={0}
            max={100}
            step={1}
            value={[pct]}
            onValueChange={(v) => {
              setPct(v[0]);
              onUse();
            }}
          />
          <p className="mt-2 text-xs text-etap-deep-400">You choose. This is not tax advice.</p>
        </div>
      </div>

      <div className="mt-7 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-etap-mint-300 bg-etap-mint-100 p-4" data-testid="calc-routed">
          <p className="text-xs font-semibold uppercase tracking-wider text-etap-deep-600">Routed to taxes</p>
          <AnimatedNumber value={routed} formatter={usd0} className="mt-1 block font-display text-2xl font-bold text-etap-deep-800" />
        </div>
        <div className="rounded-2xl border border-etap-deep-100 bg-etap-deep-50 p-4" data-testid="calc-fee">
          <p className="text-xs font-semibold uppercase tracking-wider text-etap-deep-600">ETAP fee (0.5%)</p>
          <AnimatedNumber value={fee} formatter={usd2} className="mt-1 block font-display text-2xl font-bold text-etap-deep-800" />
        </div>
        <div className="rounded-2xl border border-etap-deep-100 bg-white p-4" data-testid="calc-stays">
          <p className="text-xs font-semibold uppercase tracking-wider text-etap-deep-600">Stays in your account</p>
          <AnimatedNumber value={stays} formatter={usd0} className="mt-1 block font-display text-2xl font-bold text-etap-deep-800" />
        </div>
      </div>

      {variant === "mini" && (
        <div className="mt-6">
          <Cta to="/pricing" variant="primary" location="home-calculator" withArrow testId="calc-see-pricing">
            See full pricing
          </Cta>
        </div>
      )}
      {variant === "full" && (
        <p className="mt-5 text-xs text-etap-deep-500">
          The fee is 0.5 percent of the routed amount only. Money that stays in your account is never charged.
        </p>
      )}
    </div>
  );
}
